const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatOrderTags = (tags) =>
  (Array.isArray(tags) ? tags.filter(Boolean) : []).join(", ");

const statusClass = (status = "") => {
  if (
    status === "Inventory Available" ||
    status === "Available Now" ||
    status.startsWith("Available at ")
  ) {
    return "badge-available";
  }
  if (status.includes("Short") || status.includes("Split") || status.includes("Incoming") || status.includes("Partial")) {
    return "badge-partial";
  }
  if (status.includes("Not Available") || status === "No Stock") {
    return "badge-unavailable";
  }
  return "badge-unknown";
};

const kpiRow = (report) => {
  const kpis = report.kpis || {};
  return `
    <div class="kpis">
      <div class="kpi"><span>Total Exception Orders</span><strong>${kpis.total || 0}</strong></div>
      <div class="kpi available"><span>Inventory Available</span><strong>${kpis.inventory_available || 0}</strong></div>
      <div class="kpi partial"><span>Inventory Short</span><strong>${kpis.inventory_short || 0}</strong></div>
      <div class="kpi unavailable"><span>Inventory Not Available</span><strong>${kpis.inventory_not_available || 0}</strong></div>
      <div class="kpi unknown"><span>Inventory Data Not Available</span><strong>${kpis.inventory_data_not_available || 0}</strong></div>
    </div>
  `;
};

const skuTable = (skus = []) => `
  <table class="sku-table">
    <thead>
      <tr>
        <th>SKU</th>
        <th>Item/Product</th>
        <th>Ordered</th>
        <th>Total Inventory</th>
        <th>In Hand</th>
        <th>In Transit</th>
        <th>Immediate Shortfall</th>
        <th>Remaining Shortfall</th>
        <th>SKU Status</th>
      </tr>
    </thead>
    <tbody>
      ${skus
        .map(
          (sku) => `
        <tr>
          <td>${escapeHtml(sku.sku)}</td>
          <td>${escapeHtml(sku.name || "")}</td>
          <td>${escapeHtml(sku.ordered_quantity)}</td>
          <td>${escapeHtml(sku.quantity ?? "—")}</td>
          <td>${escapeHtml(sku.in_hand ?? "—")}</td>
          <td>${escapeHtml(sku.in_transit ?? "—")}</td>
          <td>${escapeHtml(sku.immediate_shortfall ?? "—")}</td>
          <td>${escapeHtml(sku.remaining_shortfall ?? "—")}</td>
          <td><span class="badge ${statusClass(sku.sku_status)}">${escapeHtml(sku.sku_status)}</span>
            ${sku.explanation ? `<div class="muted">${escapeHtml(sku.explanation)}</div>` : ""}
          </td>
        </tr>`,
        )
        .join("")}
    </tbody>
  </table>
`;

const warehouseFulfillmentLabel = (warehouse) => {
  if (warehouse?.can_fulfill_order) {
    return "Can fulfill whole order";
  }
  if ((warehouse?.skus || []).some((sku) => sku.data_unavailable)) {
    return "Incomplete inventory data";
  }
  return "Cannot fulfill whole order";
};

const warehouseBreakdown = (order) => {
  const warehouses = order.warehouse_breakdown || [];
  if (!warehouses.length) return "";
  return `
    <div class="warehouses">
      <h3>Warehouse inventory</h3>
      ${(warehouses)
        .map(
          (warehouse) => `
        <div class="warehouse-block${warehouse.can_fulfill_order ? " warehouse-fulfill" : ""}">
          <div class="warehouse-head">
            <strong>${escapeHtml(warehouse.warehouse_name)}</strong>
            <span class="badge ${warehouse.can_fulfill_order ? "badge-available" : "badge-unknown"}">${escapeHtml(warehouseFulfillmentLabel(warehouse))}</span>
          </div>
          <table class="sku-table warehouse-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th>Ordered</th>
                <th>On hand</th>
                <th>In transit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${(warehouse.skus || [])
                .map(
                  (sku) => `
                <tr>
                  <td>${escapeHtml(sku.sku)}</td>
                  <td>${escapeHtml(sku.ordered_quantity)}</td>
                  <td>${escapeHtml(sku.in_hand ?? "—")}</td>
                  <td>${escapeHtml(sku.in_transit ?? "—")}</td>
                  <td><span class="badge ${statusClass(sku.sku_status)}">${escapeHtml(sku.sku_status || "")}</span></td>
                </tr>`,
                )
                .join("")}
            </tbody>
          </table>
        </div>`,
        )
        .join("")}
    </div>
  `;
};

export const buildExceptionsHtml = (report) => {
  const filters = report.filters || {};
  const orders = report.orders || [];
  const generatedAt = report.generated_at_display || "";

  const rows = orders
    .map(
      (order) => `
      <details class="order">
        <summary>
          <span class="order-id">${escapeHtml(order.order_id)}</span>
          <span>${escapeHtml(order.order_date_display || "")}</span>
          <span>${escapeHtml(order.status)}</span>
          <span>${escapeHtml(order.source || "")}</span>
          <span>${escapeHtml(formatOrderTags(order.tags))}</span>
          <span>${escapeHtml(order.warehouse || "")}</span>
          <span>${escapeHtml(order.sku_count)}</span>
          <span>${escapeHtml(order.units)}</span>
          <span class="badge ${statusClass(order.inventory_status)}">${escapeHtml(order.inventory_status)}</span>
          <span>${escapeHtml(order.age_days ?? "")}</span>
        </summary>
        <div class="detail">
          ${order.inventory_analysis_informational
            ? `<p class="note">Inventory numbers are informational because this order status is normally closed.</p>`
            : ""}
          <p>${escapeHtml(order.insight || "")}</p>
          ${order.warehouse_assigned === false
            ? `<p class="muted">Inventory totals below are summed across available warehouses.</p>`
            : ""}
          ${skuTable(order.skus)}
          ${warehouseBreakdown(order)}
        </div>
      </details>`,
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Exceptions / Unfulfilled Orders Report</title>
  <style>
    :root { color-scheme: light; }
    body { font-family: "Segoe UI", Tahoma, sans-serif; margin: 24px; color: #1f2933; background: #f7f8fa; }
    h1 { margin: 0 0 8px; font-size: 22px; }
    .meta, .muted { color: #52606d; font-size: 13px; }
    .kpis { display: flex; flex-wrap: wrap; gap: 12px; margin: 16px 0 24px; }
    .kpi { background: #fff; border: 1px solid #d9e2ec; border-radius: 8px; padding: 12px 16px; min-width: 120px; }
    .kpi span { display: block; font-size: 12px; color: #627d98; }
    .kpi strong { font-size: 20px; }
    .kpi.available { border-color: #2f855a; }
    .kpi.partial { border-color: #d69e2e; }
    .kpi.unavailable { border-color: #c53030; }
    .kpi.unknown { border-color: #718096; }
    .head { display: grid; grid-template-columns: 1.1fr 1.4fr 1fr 1fr 1.2fr 1.2fr 0.5fr 0.5fr 1.5fr 0.6fr; gap: 8px; padding: 0 16px 8px; font-size: 12px; color: #627d98; font-weight: 600; }
    .order { background: #fff; border: 1px solid #d9e2ec; border-radius: 8px; margin-bottom: 8px; }
    summary { display: grid; grid-template-columns: 1.1fr 1.4fr 1fr 1fr 1.2fr 1.2fr 0.5fr 0.5fr 1.5fr 0.6fr; gap: 8px; padding: 12px 16px; cursor: pointer; align-items: center; }
    .head span, summary span { min-width: 0; overflow-wrap: anywhere; }
    .detail { padding: 0 16px 16px; border-top: 1px solid #e4e7eb; }
    .badge { display: inline-block; border-radius: 999px; padding: 2px 8px; font-size: 12px; border: 1px solid currentColor; }
    .badge-available { color: #276749; background: #f0fff4; }
    .badge-partial { color: #975a16; background: #fffff0; }
    .badge-unavailable { color: #9b2c2c; background: #fff5f5; }
    .badge-unknown { color: #4a5568; background: #edf2f7; }
    .sku-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 8px; }
    .sku-table th, .sku-table td { border-bottom: 1px solid #e4e7eb; text-align: left; padding: 8px; }
    .note { background: #fffaf0; border: 1px solid #f6e05e; padding: 8px 12px; border-radius: 6px; }
    .warehouses h3 { font-size: 14px; margin: 16px 0 8px; }
    .warehouse-block { margin-top: 8px; border: 1px solid #d9e2ec; border-radius: 8px; padding: 8px 12px 12px; background: #fff; }
    .warehouse-fulfill { border-color: #2f855a; }
    .warehouse-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 4px; }
    .warehouse-table th, .warehouse-table td { padding: 6px 8px; }
  </style>
</head>
<body>
  <h1>Exceptions / Unfulfilled Orders</h1>
  <p class="meta">These orders are not fulfilled. Do we have the inventory to fulfill them?</p>
  <p class="meta">Timezone: ${escapeHtml(filters.timezone || "")}</p>
  <p class="meta">Start: ${escapeHtml(filters.start_display || filters.start || "")}</p>
  <p class="meta">End: ${escapeHtml(filters.end_display || filters.end || "")}</p>
  <p class="meta">Statuses: ${escapeHtml((filters.statuses || []).join(", "))}</p>
  ${generatedAt ? `<p class="meta">Generated: ${escapeHtml(generatedAt)}</p>` : ""}
  ${kpiRow(report)}
  <div class="head">
    <span>Order ID</span>
    <span>Order Date/Time</span>
    <span>Status</span>
    <span>Source</span>
    <span>Tags</span>
    <span>Warehouse</span>
    <span>SKUs</span>
    <span>Units</span>
    <span>Inventory Status</span>
    <span>Age (days)</span>
  </div>
  <div id="orders">${orders.length ? rows : "<p>No matching orders.</p>"}</div>
</body>
</html>`;
};

export const downloadExceptionsHtml = (report, fileName = "exceptions-report.html") => {
  const html = buildExceptionsHtml(report);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
